import { useState } from "react";
import { Mail, Loader2, Leaf, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await api.post("/auth/request-link", { email });
      setSent(true);
    } catch {
      toast.error("Der opstod en fejl. Prøv igen.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-bg grid place-items-center px-6 py-12" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-brand-muted hover:text-brand-primary mb-8"
          data-testid="admin-back-home"
        >
          <ArrowLeft className="h-4 w-4" /> Tilbage til hjemmesiden
        </Link>

        <div className="bg-brand-surface rounded-3xl border border-brand-border p-8 lg:p-10">
          <div className="flex items-center gap-2.5 mb-8">
            <span className="grid place-items-center h-10 w-10 rounded-full bg-brand-primary text-brand-bg">
              <Leaf className="h-5 w-5" strokeWidth={1.8} />
            </span>
            <div>
              <div className="font-display text-lg font-medium">Syddansk Group</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-brand-muted">Admin Portal</div>
            </div>
          </div>

          {sent ? (
            <div data-testid="admin-login-sent">
              <div className="h-14 w-14 rounded-full bg-brand-soft grid place-items-center mb-4">
                <Mail className="h-6 w-6 text-brand-primary" />
              </div>
              <h1 className="font-display text-3xl tracking-tight text-brand-text">Tjek din indbakke</h1>
              <p className="mt-3 text-brand-muted leading-relaxed text-sm">
                Hvis <strong>{email}</strong> er registreret som admin, har vi sendt et login-link. Linket udløber om 15 minutter.
              </p>
              <button
                onClick={() => { setSent(false); setEmail(""); }}
                data-testid="admin-login-retry"
                className="mt-6 text-sm text-brand-primary underline underline-offset-4 hover:opacity-80"
              >
                Send til en anden e-mail
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="admin-login-form">
              <div>
                <h1 className="font-display text-3xl tracking-tight text-brand-text">Log ind</h1>
                <p className="mt-2 text-sm text-brand-muted">
                  Indtast din admin-e-mail. Vi sender dig et sikkert login-link.
                </p>
              </div>

              <div>
                <Label htmlFor="admin-email" className="text-sm">E-mail</Label>
                <Input
                  id="admin-email"
                  type="email"
                  required
                  data-testid="admin-login-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="din@email.dk"
                  className="mt-1.5 h-12 bg-brand-bg border-brand-border"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="admin-login-submit"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-primary text-brand-bg px-6 py-3.5 font-medium hover:bg-brand-hover disabled:opacity-60"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Sender...</>
                ) : (
                  <>Send login-link</>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
